/**
 * Low-segment, physically scaled chamfered box for industrial rig parts.
 *
 * A stock box has infinitely sharp 90-degree edges. Real fabricated steel never does:
 * rolled sections, weld preparation, paint thickness, impacts and corrosion all create
 * a small transition that catches light. This mesh keeps the broad faces planar, adds
 * one flat chamfer along every edge, and closes each corner with a small triangle.
 *
 * The bevel is specified in world metres even though the returned mesh remains a unit
 * box. The caller can therefore reuse its existing transform and accumulation path.
 */

import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Matrix } from '@babylonjs/core/Maths/math.vector';

type Point3 = readonly [number, number, number];

export interface BeveledBoxResult {
  data: VertexData;
  /** Per-vertex 0..1 mask: 0 broad face, ~0.7 edge chamfer, 1 corner. */
  edgeWeights: number[];
  bevelMetres: number;
}

function axisLength(transform: Matrix, axis: number): number {
  const v = axis === 0
    ? new Vector3(1, 0, 0)
    : axis === 1
      ? new Vector3(0, 1, 0)
      : new Vector3(0, 0, 1);
  return Vector3.TransformNormal(v, transform).length();
}

function projectedUv(
  point: Point3,
  normal: Point3,
  inner: Point3,
): readonly [number, number] {
  const map = (axis: number, flip: boolean): number => {
    const value = point[axis] / (inner[axis] * 2);
    return 0.5 + (flip ? -value : value);
  };
  const nx = Math.abs(normal[0]);
  const ny = Math.abs(normal[1]);
  const nz = Math.abs(normal[2]);
  if (nx > ny && nx > nz) {
    // Match Babylon's box islands exactly: ±X uses Z across U and Y up V.
    return [map(2, normal[0] > 0), map(1, false)];
  }
  if (ny > nz) {
    // ±Y uses X across U and Z up V.
    return [map(0, normal[1] > 0), map(2, false)];
  }
  // ±Z uses X across U and Y up V.
  return [map(0, false), map(1, normal[2] > 0)];
}

function outwardNormal(
  axisLengths: readonly [number, number, number],
  components: Point3,
): Point3 {
  // MeshAccumulator currently transforms normals with the object's scale matrix.
  // Dividing by that scale here makes a two-axis chamfer land at 45 degrees in world
  // space even on a beam that is twenty metres long and half a metre thick.
  const x = components[0] / axisLengths[0];
  const y = components[1] / axisLengths[1];
  const z = components[2] / axisLengths[2];
  const length = Math.hypot(x, y, z) || 1;
  return [x / length, y / length, z / length];
}

export function createBeveledBoxVertexData(
  transform: Matrix,
  maxBevelMetres = 0.12,
): BeveledBoxResult {
  const lengths = [
    axisLength(transform, 0),
    axisLength(transform, 1),
    axisLength(transform, 2),
  ] as const;
  const minLength = Math.min(...lengths);
  if (!Number.isFinite(minLength) || minLength <= 0) {
    throw new RangeError('beveled box transform must have three positive axes');
  }

  // Ten percent of the thinnest section reads as rolled/painted fabrication. The hard
  // centimetre limits prevent huge modules receiving cartoonishly broad bevels and keep
  // tiny fittings from collapsing their central faces.
  const bevelMetres = Math.min(
    maxBevelMetres,
    Math.max(0.008, minLength * 0.10),
    minLength * 0.22,
  );
  const inner = [
    0.5 - bevelMetres / lengths[0],
    0.5 - bevelMetres / lengths[1],
    0.5 - bevelMetres / lengths[2],
  ] as const;

  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const edgeWeights: number[] = [];

  const addPolygon = (
    sourcePoints: Point3[],
    normalComponents: Point3,
    edgeWeight: number,
  ): void => {
    const normal = outwardNormal(lengths, normalComponents);
    const points = [...sourcePoints];
    const a = Vector3.FromArray(points[0] as Point3);
    const b = Vector3.FromArray(points[1] as Point3);
    const c = Vector3.FromArray(points[2] as Point3);
    const windingNormal = Vector3.Cross(b.subtract(a), c.subtract(a));
    // Babylon's left-handed primitive convention winds the outside clockwise: the
    // geometric cross product therefore points opposite the supplied outward normal.
    if (Vector3.Dot(windingNormal, Vector3.FromArray(normal)) > 0) points.reverse();

    const base = positions.length / 3;
    for (const point of points) {
      positions.push(point[0], point[1], point[2]);
      normals.push(normal[0], normal[1], normal[2]);
      const uv = projectedUv(point, normal, inner);
      uvs.push(uv[0], uv[1]);
      edgeWeights.push(edgeWeight);
    }
    for (let i = 1; i < points.length - 1; i++) {
      indices.push(base, base + i, base + i + 1);
    }
  };

  // Six broad planar faces. Their edges stop one bevel-width before the ideal cube
  // corners, keeping the photographic scan undistorted across the dominant area.
  for (let axis = 0; axis < 3; axis++) {
    const tangentA = (axis + 1) % 3;
    const tangentB = (axis + 2) % 3;
    for (const sign of [-1, 1]) {
      const points: Point3[] = [];
      for (const [sa, sb] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
        const p = [0, 0, 0];
        p[axis] = sign * 0.5;
        p[tangentA] = sa * inner[tangentA];
        p[tangentB] = sb * inner[tangentB];
        points.push(p as unknown as Point3);
      }
      const normal = [0, 0, 0];
      normal[axis] = sign;
      addPolygon(points, normal as unknown as Point3, 0);
    }
  }

  // Twelve edge strips. Their world normals bisect the two meeting face normals, which
  // is the light-roll cue absent from the stock box.
  for (let runAxis = 0; runAxis < 3; runAxis++) {
    const sideA = (runAxis + 1) % 3;
    const sideB = (runAxis + 2) % 3;
    for (const signA of [-1, 1]) {
      for (const signB of [-1, 1]) {
        const points: Point3[] = [];
        for (const runSign of [-1, 1]) {
          const onA = [0, 0, 0];
          onA[runAxis] = runSign * inner[runAxis];
          onA[sideA] = signA * 0.5;
          onA[sideB] = signB * inner[sideB];
          points.push(onA as unknown as Point3);
        }
        for (const runSign of [1, -1]) {
          const onB = [0, 0, 0];
          onB[runAxis] = runSign * inner[runAxis];
          onB[sideA] = signA * inner[sideA];
          onB[sideB] = signB * 0.5;
          points.push(onB as unknown as Point3);
        }
        const normal = [0, 0, 0];
        normal[sideA] = signA;
        normal[sideB] = signB;
        addPolygon(points, normal as unknown as Point3, 0.68);
      }
    }
  }

  // Eight triangular corner caps. Flat caps suit battered fabricated steel better than
  // a glossy high-segment consumer-product roundover, and cost only eight triangles.
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      for (const sz of [-1, 1]) {
        addPolygon([
          [sx * 0.5, sy * inner[1], sz * inner[2]],
          [sx * inner[0], sy * 0.5, sz * inner[2]],
          [sx * inner[0], sy * inner[1], sz * 0.5],
        ], [sx, sy, sz], 1);
      }
    }
  }

  const data = new VertexData();
  data.positions = positions;
  data.normals = normals;
  data.uvs = uvs;
  data.indices = indices;
  return { data, edgeWeights, bevelMetres };
}
